import { RuntimeMediaQuery } from '@duda-co/responsive-styles/RuntimeMediaQuery';
import type { ResponsiveStylesStructured } from '@duda-co/responsive-styles/types/ResponsiveStylesTypes';
import { ResponsiveStylesProp } from 'client/widget-components/responsive-styles/responsiveStylesService';
import { toCSSObject } from 'client/widget-components/responsive-styles/toCSSObject';
import type { TextareaHTMLAttributes } from 'react';
import React, { useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';

export interface TextAreaProps
    extends TextareaHTMLAttributes<HTMLTextAreaElement> {
    value: string | undefined;
    styles?: ResponsiveStylesProp;
    increaseSpecificity?: boolean;
    autosize?: boolean;
}

const StyledTextArea = styled.textarea<TextAreaProps>(
    ({ styles, increaseSpecificity }) =>
        toCSSObject([resetStyles, styles], increaseSpecificity)
);

const resetStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        padding: 'unset',
        margin: 'unset',
        backgroundColor: 'unset',
        border: '1px solid #000000',
        color: '#000000',
        minHeight: 'unset',
        height: 'unset',
        fontSize: 'unset',
        resize: 'none',
    },
};

const TextArea = ({ autosize, value, ...props }: TextAreaProps) => {
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    const initialHeightRef = useRef<number | null>(null);

    const updateHeight = useCallback(() => {
        const textArea = textAreaRef.current;

        if (textArea !== null) {
            const currentHeight = textArea?.clientHeight ?? null;

            if (initialHeightRef.current === null) {
                initialHeightRef.current = currentHeight;
            }

            textArea.style.height = 'auto';
            const scrollHeight = textArea.scrollHeight;

            // restricting min height to be no less than initial height
            const heightWithMinCap = Math.max(
                initialHeightRef.current,
                scrollHeight
            );

            // restricting max height to be no more than 300px
            const heightWithMaxCap = Math.min(heightWithMinCap, 300);

            textArea.style.height = `${heightWithMaxCap}px`;
        }
    }, []);

    useEffect(() => {
        if (autosize) {
            updateHeight();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, autosize]);

    return <StyledTextArea ref={textAreaRef} value={value} {...props} />;
};

export default TextArea;
